import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TablePagination,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  IconButton,
  Divider,
} from '@mui/material';
import { hasWritePermission } from '../../utils/menuFilter';
import {
  Search,
  Cancel,
  Visibility,
  Receipt,
  Person,
  Home,
  AttachMoney,
  Warning,
  CheckCircle,
  Business,
} from '@mui/icons-material';

const CancelPlotBooking = () => {
  const canWriteCancelBooking = hasWritePermission('plot-booking.cancel-plot-booking') || hasWritePermission('booking.cancel-plot-booking');
  const [searchForm, setSearchForm] = useState({
    bookingId: '',
    plotNo: '',
    customerName: '',
    mobileNo: '',
  });

  const [showBookingList, setShowBookingList] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [searchLoading, setSearchLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Sample booking data for cancellation
  const [bookingData] = useState([
    {
      id: 1,
      bookingId: 'BK001',
      plotNo: 'P101',
      customerName: 'John Smith',
      mobileNo: '9876543210',
      siteName: 'Green Valley',
      sectorName: 'Sector A',
      blockName: 'Block 1',
      plotSize: '30x40 ft',
      plotArea: '1200 sq ft',
      bookingDate: '2025-01-15',
      totalAmount: '₹54,00,000',
      paidAmount: '₹5,40,000',
      balanceAmount: '₹48,60,000',
      status: 'Active',
      paymentPlan: 'Installment',
    },
    {
      id: 2,
      bookingId: 'BK002',
      plotNo: 'P205',
      customerName: 'Sarah Johnson',
      mobileNo: '9876543211',
      siteName: 'Hill View',
      sectorName: 'Sector B',
      blockName: 'Block 2',
      plotSize: '35x45 ft',
      plotArea: '1575 sq ft',
      bookingDate: '2025-02-20',
      totalAmount: '₹81,90,000',
      paidAmount: '₹20,47,500',
      balanceAmount: '₹61,42,500',
      status: 'Active',
      paymentPlan: 'Down Payment',
    },
    {
      id: 3,
      bookingId: 'BK003',
      plotNo: 'P321',
      customerName: 'Michael Brown',
      mobileNo: '9876543212',
      siteName: 'Paradise Heights',
      sectorName: 'Sector C',
      blockName: 'Block 3',
      plotSize: '40x50 ft',
      plotArea: '2000 sq ft',
      bookingDate: '2025-03-10',
      totalAmount: '₹1,20,00,000',
      paidAmount: '₹12,00,000',
      balanceAmount: '₹1,08,00,000',
      status: 'Active',
      paymentPlan: 'Installment',
    },
    {
      id: 4,
      bookingId: 'BK004',
      plotNo: 'P456',
      customerName: 'Emily Davis',
      mobileNo: '9876543213',
      siteName: 'Royal Gardens',
      sectorName: 'Sector D',
      blockName: 'Block 4',
      plotSize: '45x55 ft',
      plotArea: '2475 sq ft',
      bookingDate: '2025-04-05',
      totalAmount: '₹1,85,62,500',
      paidAmount: '₹37,12,500',
      balanceAmount: '₹1,48,50,000',
      status: 'Active',
      paymentPlan: 'Full Payment',
    },
    {
      id: 5,
      bookingId: 'BK005',
      plotNo: 'P789',
      customerName: 'David Wilson',
      mobileNo: '9876543214',
      siteName: 'Green Valley',
      sectorName: 'Sector B',
      blockName: 'Block 2',
      plotSize: '32x48 ft',
      plotArea: '1536 sq ft',
      bookingDate: '2025-05-12',
      totalAmount: '₹73,72,800',
      paidAmount: '₹7,37,280',
      balanceAmount: '₹66,35,520',
      status: 'Active',
      paymentPlan: 'Installment',
    },
    {
      id: 6,
      bookingId: 'BK006',
      plotNo: 'P654',
      customerName: 'Lisa Anderson',
      mobileNo: '9876543215',
      siteName: 'Hill View',
      sectorName: 'Sector B',
      blockName: 'Block 2',
      plotSize: '38x52 ft',
      plotArea: '1976 sq ft',
      bookingDate: '2025-06-18',
      totalAmount: '₹1,02,75,200',
      paidAmount: '₹25,68,800',
      balanceAmount: '₹77,06,400',
      status: 'Active',
      paymentPlan: 'Down Payment',
    },
  ]);

  const cancelReasons = [
    'Customer Request',
    'Financial Issues',
    'Change of Plans',
    'Documentation Issues',
    'Legal Issues',
    'Force Majeure',
    'Other',
  ];

  const handleInputChange = (field, value) => {
    setSearchForm({
      ...searchForm,
      [field]: value,
    });
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    setErrors({});

    // Simulate API call
    setTimeout(() => {
      setShowBookingList(true);
      setPage(0);
      setSuccessMessage('');
      console.log('Searching with criteria:', searchForm);
      setSearchLoading(false);
    }, 1500);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleCancelBooking = (booking) => {
    setSelectedBooking(booking);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason) {
      setErrors({ cancelReason: 'Please select a cancellation reason' });
      return;
    }

    if (!selectedBooking) return;

    setCancelLoading(true);

    // Simulate API call
    setTimeout(() => {
      setSuccessMessage(`Booking ${selectedBooking.bookingId} has been successfully cancelled.`);
      setCancelDialogOpen(false);
      setCancelReason('');
      setSelectedBooking(null);
      setErrors({});
      console.log('Cancelled booking:', selectedBooking.bookingId, 'Reason:', cancelReason);
      setCancelLoading(false);
    }, 2000);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason('');
    setSelectedBooking(null);
    setErrors({});
  };

  const handleCancelReasonChange = (e) => {
    setCancelReason(e.target.value);
    if (errors.cancelReason) {
      setErrors({ ...errors, cancelReason: '' });
    }
  };

  const getFilteredBookingData = () => {
    if (!showBookingList) return [];

    let filteredData = bookingData.filter(booking => booking.status === 'Active');

    // Filter by search form criteria
    if (searchForm.bookingId) {
      filteredData = filteredData.filter(booking =>
        booking.bookingId.toLowerCase().includes(searchForm.bookingId.toLowerCase())
      );
    }
    if (searchForm.plotNo) {
      filteredData = filteredData.filter(booking =>
        booking.plotNo.toLowerCase().includes(searchForm.plotNo.toLowerCase())
      );
    }
    if (searchForm.customerName) {
      filteredData = filteredData.filter(booking =>
        booking.customerName.toLowerCase().includes(searchForm.customerName.toLowerCase())
      );
    }
    if (searchForm.mobileNo) {
      filteredData = filteredData.filter(booking =>
        booking.mobileNo.includes(searchForm.mobileNo)
      );
    }

    // Filter by table search query
    if (tableSearchQuery.trim()) {
      filteredData = filteredData.filter(booking =>
        Object.values(booking).some(value =>
          String(value).toLowerCase().includes(tableSearchQuery.toLowerCase())
        )
      );
    }

    return filteredData;
  };

  const getSearchSummary = () => {
    const criteria = [];
    if (searchForm.bookingId) criteria.push(`Booking ID: "${searchForm.bookingId}"`);
    if (searchForm.plotNo) criteria.push(`Plot No: "${searchForm.plotNo}"`);
    if (searchForm.customerName) criteria.push(`Customer: "${searchForm.customerName}"`);
    if (searchForm.mobileNo) criteria.push(`Mobile: "${searchForm.mobileNo}"`);

    return criteria.length > 0 ? criteria.join(', ') : 'All Active Bookings';
  };

  // Calculate statistics
  const activeBookings = bookingData.filter(booking => booking.status === 'Active');
  const totalActiveAmount = activeBookings.reduce((sum, booking) => {
    const amount = parseFloat(booking.totalAmount.replace(/[₹,]/g, ''));
    return sum + amount;
  }, 0);
  const totalPaidAmount = activeBookings.reduce((sum, booking) => {
    const amount = parseFloat(booking.paidAmount.replace(/[₹,]/g, ''));
    return sum + amount;
  }, 0);

  return (
    <Box sx={{ p: 3, backgroundColor: '#fafafa', minHeight: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
          Cancel Plot Booking
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search and cancel active plot bookings with proper documentation
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: '#e3f2fd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Receipt sx={{ color: '#1976d2', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                    {activeBookings.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Bookings
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: '#fff3e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <AttachMoney sx={{ color: '#f57c00', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                    ₹{(totalActiveAmount / 100000).toFixed(1)}L
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Value
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: '#e8f5e9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CheckCircle sx={{ color: '#2e7d32', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                    ₹{(totalPaidAmount / 100000).toFixed(1)}L
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Paid Amount
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: '#ffebee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Warning sx={{ color: '#d32f2f', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                    {((totalActiveAmount - totalPaidAmount) / 100000).toFixed(1)}L
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Amount
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid> */}

      {/* Search Section */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Search sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Search Active Bookings
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Booking ID"
                variant="outlined"
                fullWidth
                value={searchForm.bookingId}
                onChange={(e) => handleInputChange('bookingId', e.target.value)}
                placeholder="Enter Booking ID"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Plot Number"
                variant="outlined"
                fullWidth
                value={searchForm.plotNo}
                onChange={(e) => handleInputChange('plotNo', e.target.value)}
                placeholder="Enter Plot Number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Home color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Customer Name"
                variant="outlined"
                fullWidth
                value={searchForm.customerName}
                onChange={(e) => handleInputChange('customerName', e.target.value)}
                placeholder="Enter Customer Name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Mobile Number"
                variant="outlined"
                fullWidth
                value={searchForm.mobileNo}
                onChange={(e) => handleInputChange('mobileNo', e.target.value)}
                placeholder="Enter Mobile Number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Business color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  disabled={searchLoading}
                  startIcon={<Search />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '15px',
                    boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                    borderRadius: 2
                  }}
                >
                  {searchLoading ? 'Searching...' : 'Search Bookings'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Active Bookings Table */}
      {showBookingList && (
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box display="flex" alignItems="center" gap={2}>
                <Warning sx={{ color: '#f57c00', fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                  Active Bookings for Cancellation
                </Typography>
              </Box>
              <TextField
                label="Search Bookings..."
                variant="outlined"
                size="small"
                value={tableSearchQuery}
                onChange={(e) => setTableSearchQuery(e.target.value)}
                sx={{
                  width: 300,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Search Criteria: {getSearchSummary()}
            </Typography>
            <Typography variant="body2" color="primary" sx={{ mb: 3, fontWeight: 600 }}>
              Found {getFilteredBookingData().length} active booking(s) available for cancellation
            </Typography>

            {getFilteredBookingData().length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Booking Info</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Customer</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Plot Details</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Location</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Financial Details</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredBookingData()
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((booking, index) => (
                        <TableRow
                          key={booking.id}
                          sx={{
                            '&:hover': { backgroundColor: '#f9fafb' },
                            '& td': { py: 2 }
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, color: '#000' }}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>

                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                                {booking.bookingId}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {booking.bookingDate}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                                {booking.customerName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {booking.mobileNo}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                                Plot {booking.plotNo}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {booking.plotSize}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {booking.plotArea}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                                {booking.siteName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {booking.sectorName}, {booking.blockName}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                                Total: {booking.totalAmount}
                              </Typography>
                              <Typography variant="caption" color="success.main">
                                Paid: {booking.paidAmount}
                              </Typography>
                              <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                                Balance: {booking.balanceAmount}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={booking.status}
                              color="success"
                              size="small"
                              sx={{ fontWeight: 600, fontSize: '12px' }}
                            />
                          </TableCell>

                          <TableCell align="center">
                            <Tooltip title="Cancel Booking">
                              <IconButton
                                color="error"
                                onClick={() => handleCancelBooking(booking)}
                                sx={{
                                  '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.1)' }
                                }}
                              >
                                <Cancel />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <TablePagination
                  
                  component="div"
                  count={getFilteredBookingData().length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ borderTop: '1px solid #e0e0e0' }}
                />
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Alert severity="info">
                  <Typography variant="h6" gutterBottom>
                    No Active Bookings Found
                  </Typography>
                  <Typography variant="body2">
                    No active bookings match your search criteria: {getSearchSummary()}
                  </Typography>
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cancel Booking Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          fontSize: '24px',
          fontWeight: 700,
          color: '#000',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Warning sx={{ color: '#f57c00', fontSize: 28 }} />
            Cancel Plot Booking
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedBooking && (
            <Box>
              <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Are you sure you want to cancel this booking? This action cannot be undone.
                </Typography>
              </Alert>

              {/* Booking Information */}
              <Card sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                  Booking Information 123
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Booking ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedBooking.bookingId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Plot Number</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedBooking.plotNo}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Customer Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedBooking.customerName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Mobile Number</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedBooking.mobileNo}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Site Location</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedBooking.siteName} - {selectedBooking.sectorName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Booking Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedBooking.bookingDate}
                    </Typography>
                  </Grid>
                </Grid>
              </Card>

              {/* Financial Summary */}
              <Card sx={{ p: 2, backgroundColor: '#fff3e0', border: '1px solid #ffcc02', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#f57c00' }}>
                  Financial Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box textAlign="center">
                      <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#000' }}>
                        {selectedBooking.totalAmount}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box textAlign="center">
                      <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                        {selectedBooking.paidAmount}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box textAlign="center">
                      <Typography variant="body2" color="text.secondary">Balance Amount</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
                        {selectedBooking.balanceAmount}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Card>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">Cancellation Reason</Typography>
              </Divider>

              <FormControl fullWidth error={!!errors.cancelReason}>
                <InputLabel>Cancellation Reason *</InputLabel>
                <Select
                  value={cancelReason}
                  label="Cancellation Reason *"
                  onChange={handleCancelReasonChange}
                  sx={{ borderRadius: 2 }}
                >
                  {cancelReasons.map((reason) => (
                    <MenuItem key={reason} value={reason}>
                      {reason}
                    </MenuItem>
                  ))}
                </Select>
                {errors.cancelReason && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.cancelReason}
                  </Typography>
                )}
              </FormControl>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCloseCancelDialog}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Keep Booking
          </Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
            disabled={!cancelReason || cancelLoading}
            startIcon={<Cancel />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
            }}
          >
            {cancelLoading ? 'Cancelling...' : 'Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CancelPlotBooking;